import React, { memo, useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";

import { Button, CreateAlert, Icon, ITableStructureItem } from "../../modules";
import { TableFilterInputText } from "../../components/table-filter-inputs";
import { SingerService } from "../../services/singer";
import { TableSimplePagination } from "../../modules/table/TableSimplePagination";
import { ConfirmDeletePopup } from "../../components/confirm-delete-popup";
import ImportButton from "../../components/import-button";
import { Image } from "../../components/image";
import { TableFilterInputSelect } from "../../components/table-filter-inputs/select";
import { LanguageService } from "../../services/language/language.service";

const GENDER_LIST: any = {
  male: {
    label: "Nam",
    value: "male"
  },
  female: {
    label: "Nữ",
    value: "female"
  },
  band: {
    label: "Ban nhạc",
    value: "band"
  }
}

const SingersList = () => {
  const [dataList, setDataList] = useState<any>();
  const [filterParams, setFilterParams] = useState<any>({
    page: 1,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [languageList, setLanguageList] = useState<{ label: string, value: string | number }[]>([]);
  const [activeDelete, setActiveDelete] = useState<{
    open: boolean;
    data: any;
  }>({
    open: false,
    data: undefined,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const data = await SingerService.getList(filterParams);
    setDataList(data);
    setLoading(false);
  }, [filterParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (data: any) => {
    try {
      const res = await SingerService.delete(data?.data?.singerId);

      if (res?.success) {
        setActiveDelete({ open: false, data: undefined });
        const _params = { page: 1, pageSize: 10 };
        setFilterParams({ ..._params });
        CreateAlert({
          type: "success",
          message: "Xóa dữ liệu thành công",
        });
      }
    } catch (error: any) {
      setActiveDelete({ open: false, data: undefined });
      CreateAlert({
        type: "danger",
        message: error.message || "Xóa dữ liệu thất bại",
      });
    }
  };

  const structure: ITableStructureItem[] = [
    {
      name: "Mã ca sĩ",
      key: "singerId",
      render: (item: any) => item?.singerId || "--",
    },
    {
      name: "Hình ảnh",
      key: "fileName",
      render: (item: any) => (
        <Image src={item?.fileName} style={{ maxWidth: 120 }} />
      ),
    },
    {
      name: "Tên ca sĩ",
      key: "name",
      render: (item: any) => item?.name || "--",
    },
    {
      name: "Tên tìm kiếm",
      key: "nameForSearch",
      render: (item: any) => item?.nameForSearch || "--",
    },
    {
      name: "Tên ngắn",
      key: "shortName",
      render: (item: any) => item?.shortName || "--",
    },
    {
      name: "Thể loại",
      key: "genreName",
      render: (item: any) => item?.genreName || "--",
    },
    {
      name: "Giới tính",
      key: "gender",
      render: (item: any) => GENDER_LIST?.[item?.gender]?.label || "--",
    },
    {
      name: "Ngôn ngữ",
      key: "language",
      render: (item: any) => item?.language || "--",
    },
    {
      name: "Thao tác",
      key: "singerId",
      render: (item: any) => {
        return (
          <div className="btnGroup">
            <span
              className="btn"
              onClick={() =>
                setActiveDelete({
                  open: true,
                  data: item,
                })
              }
            >
              <Icon.Delete />
            </span>
          </div>
        );
      },
    },
  ];

  const filters = [
    {
      name: "Tìm kiếm theo mã ca sĩ, tên ca sĩ",
      key: "searchString",
      input: (s: any) => (
        <TableFilterInputText
          {...s}
          params={{
              ...s?.params,
              searchString: filterParams?.searchString,
          }}
        />
      ),
    },
    {
      name: "Giới tính",
      key: "gender",
      input: (s: any) => {
        return (
          <TableFilterInputSelect
            {...s}
            params={{ ...s?.params, gender: filterParams?.gender }}
            options={Object.values(GENDER_LIST)}
          />
        );
      },
    },
    {
      name: "Sắp xếp",
      key: "sortValue",
      input: (s: any) => {
        return (
          <TableFilterInputSelect
            {...s}
            onChange={(e) => {
              const { sortValue } = e

              let _params: any = {
                sortValue
              }

              if (sortValue === 1) {
                _params = {
                  ..._params,
                  orderBy: "name",
                  orderDirection: "ASC",
                }
              }

              if (sortValue === 2) {
                _params = {
                  ..._params,
                  orderBy: "name",
                  orderDirection: "DESC",
                }
              }

              if (sortValue === 3) {
                _params = {
                  ..._params,
                  orderBy: "priority",
                  orderDirection: "ASC",
                }
              }

              setFilterParams((prev: any) => {
                if (!sortValue) {
                  delete prev.orderBy
                  delete prev.orderDirection

                  return {
                    ...prev,
                    ..._params
                  }
                }

                return {
                  ...prev,
                  ..._params
                }
              })
            }}
            params={{ ...s?.params, sortValue: filterParams?.sortValue }}
            options={[
              {
                label: "A-Z",
                value: 1
              },
              {
                label: "Z-A",
                value: 2
              },
              {
                label: "HOT",
                value: 3
              }
            ]}
          />
        );
      },
    },
    {
      name: "Ngôn ngữ",
      key: "language",
      input: (s: any) => {
        return (
          <TableFilterInputSelect
            {...s}
            params={{ ...s?.params, language: filterParams?.language }}
            options={languageList}
          />
        );
      },
    },
    {
      name: "Làm mới",
      input: () => (
        <Button
          label="Xác nhận"
          buttonType="info"
          onClick={() => setFilterParams({ page: 1, pageSize: 10 })}
        />
      ),
    },
  ]  

  const handleExportExcel = async () => {
    return new Promise(async (resolve) => {
      try {
        const response = await SingerService.getList({
          ...filterParams,
          page: 1,
          pageSize: 1000000,
        });

        const data = response.data;

        const fileHead = structure.map((v) => v.name);
        const dataExport = [
          fileHead,
          ...data.map((item: any) =>
            structure.map((column, index) => {
              if (!column.key) return "";
              return item[column.key];
            })
          ),
        ];

        const ws = XLSX.utils.aoa_to_sheet(dataExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "SheetJS");

        const now = new Date();
        XLSX.writeFile(
          wb,
          `Singer List ${now.toLocaleDateString().replace(/\//g, "-")} ${now
            .toLocaleTimeString()
            .replace(/:/g, "-")}.xlsx`,
          { type: "binary" }
        );

        resolve(
          CreateAlert({
            type: "success",
            message: "Export data success.",
          })
        );
      } catch (error: any) {
        resolve(CreateAlert({ type: "danger", message: error.message }));
      }
    });
  };

  const handleImport = async (form: FormData, cb: any) => {
    try {
      const res: any = await SingerService.import(form);

      if (res?.success) {
        const _params = { page: 1, pageSize: 10 };
        setFilterParams({ ..._params });
        cb();
        CreateAlert({
          type: "success",
          message: "Nhập dữ liệu thành công",
        });
      }
    } catch (error: any) {
      cb();
      CreateAlert({
        type: "danger",
        message: error.message || "Nhập dữ liệu thất bại",
      });
    }
  };

  useEffect(() => {
    const fetchLanguage = async () => {
      const res = await LanguageService.getList({ pageSize: 1000000 })
      if (res?.data) setLanguageList(res?.data?.map((x: any) => ({ label: x, value: x })))
    }

    Promise.all([fetchLanguage()])
  }, [])

  return (
    <>
      <div className="firmwares-container">
        <div className="title">Quản lý ca sỹ</div>

        <div className="content">
          <div className="wrap-action-header">
            <ImportButton
              fileUrl="/templates/Template_import-singer.xls"
              onImport={handleImport}
            />

            <Button
              className="mb20"
              label="Xuất excel"
              buttonType="success"
              onClick={handleExportExcel}
            />
          </div>

          <TableSimplePagination
            dataTable={{
              dataList: dataList,
              filterParams: filterParams,
              totalPage: dataList
                ? Math.ceil(dataList?.count / filterParams?.pageSize)
                : 0,
              loading,
            }}
            setParams={setFilterParams}
            filters={filters}
            structure={structure}
          />
        </div>
      </div>

      {activeDelete?.open && (
        <ConfirmDeletePopup
          onClose={() => setActiveDelete({ open: false, data: undefined })}
          onConfirm={() => handleDelete(activeDelete)}
        />
      )}
    </>
  );
};

export default memo(SingersList);
